import React, { useEffect, useRef } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { Shield, Clock, Sofa, Calendar, Globe } from 'lucide-react';

const WhyChoose = () => {
  const { t } = useLanguage();
  const { isDark } = useTheme();
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    const cards = sectionRef.current?.querySelectorAll('.why-card');
    cards?.forEach((card) => observer.observe(card));

    return () => observer.disconnect();
  }, []);

  const values = [
    {
      icon: Shield,
      titleKey: 'why.discretion.title',
      descKey: 'why.discretion.description',
      testId: 'why-discretion',
    },
    {
      icon: Clock,
      titleKey: 'why.punctuality.title',
      descKey: 'why.punctuality.description',
      testId: 'why-punctuality',
    },
    {
      icon: Sofa,
      titleKey: 'why.comfort.title',
      descKey: 'why.comfort.description',
      testId: 'why-comfort',
    },
    {
      icon: Calendar,
      titleKey: 'why.reservation.title',
      descKey: 'why.reservation.description',
      testId: 'why-reservation',
    },
  ];

  return (
    <section
      ref={sectionRef}
      data-testid="why-section"
      className={`relative py-24 md:py-32 transition-colors duration-500 ${
        isDark ? 'bg-[#0D0D0D]' : 'bg-[#F8F5EE]'
      }`}
    >
      {/* Grain texture */}
      <div 
        className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
        }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        {/* Section Title */}
        <div className="text-center mb-16 md:mb-20">
          <h2 
            data-testid="why-title"
            className={`font-playfair text-3xl sm:text-4xl lg:text-5xl mb-6 ${
              isDark ? 'text-white' : 'text-[#1A1A1A]'
            }`}
          >
            {t('why.title').replace('HB Transfert', '')}
            <span className="text-[#D4AF37]">HB Transfert</span>
          </h2>
          <div className="flex items-center justify-center gap-3">
            <div className={`w-12 h-px ${isDark ? 'bg-[#D4AF37]/50' : 'bg-[#D4AF37]/70'}`} />
            <div className="w-2 h-2 rotate-45 bg-[#D4AF37]" />
            <div className={`w-12 h-px ${isDark ? 'bg-[#D4AF37]/50' : 'bg-[#D4AF37]/70'}`} />
          </div>
        </div>

        {/* Values Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8 mb-12">
          {values.map((value, index) => (
            <div
              key={index}
              data-testid={value.testId}
              className={`why-card group text-center p-8 md:p-10 rounded-sm transition-all duration-500 opacity-0 translate-y-8 border ${
                isDark 
                  ? 'bg-gradient-to-b from-[#1A1A1A]/50 to-transparent border-[#D4AF37]/10 hover:border-[#D4AF37]/40' 
                  : 'bg-white/50 border-[#D4AF37]/20 hover:border-[#D4AF37]/50'
              } hover:shadow-[0_15px_50px_rgba(212,175,55,0.1)]`}
              style={{ animationDelay: `${index * 0.15}s` }}
            >
              {/* Icon with animated ring */}
              <div className="mb-6 inline-flex items-center justify-center relative">
                <div className={`absolute inset-0 w-20 h-20 rounded-full border transition-all duration-500 ${
                  isDark 
                    ? 'border-[#D4AF37]/20 group-hover:border-[#D4AF37]/50 group-hover:scale-110' 
                    : 'border-[#D4AF37]/30 group-hover:border-[#D4AF37]/60 group-hover:scale-110'
                }`} style={{ margin: '-0.5rem' }} />
                <div className={`w-16 h-16 rounded-full flex items-center justify-center transition-all duration-500 ${
                  isDark 
                    ? 'bg-[#D4AF37]/10 group-hover:bg-[#D4AF37]/20' 
                    : 'bg-[#D4AF37]/15 group-hover:bg-[#D4AF37]/25'
                }`}>
                  <value.icon className="w-7 h-7 text-[#D4AF37] group-hover:scale-110 transition-transform duration-300" />
                </div>
              </div>

              {/* Title */}
              <h3 className={`font-playfair text-xl md:text-2xl mb-3 ${
                isDark ? 'text-white' : 'text-[#1A1A1A]'
              }`}>
                {t(value.titleKey)}
              </h3>

              {/* Description */}
              <p className={`text-sm leading-relaxed ${
                isDark ? 'text-[#F8F5EE]/60' : 'text-[#1A1A1A]/60'
              }`}>
                {t(value.descKey)}
              </p>
            </div>
          ))}
        </div>

        {/* Languages Badge */}
        <div 
          data-testid="why-languages"
          className={`flex items-center justify-center gap-3 py-6 px-8 mx-auto max-w-md rounded-full border ${
            isDark 
              ? 'bg-[#D4AF37]/5 border-[#D4AF37]/20' 
              : 'bg-[#D4AF37]/10 border-[#D4AF37]/30'
          }`}
        >
          <Globe className="w-5 h-5 text-[#D4AF37]" />
          <span className={`text-sm tracking-wide ${
            isDark ? 'text-[#F8F5EE]/80' : 'text-[#1A1A1A]/80'
          }`}>
            {t('why.languages')}
          </span>
        </div>
      </div>

      {/* Section separator */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#D4AF37]/30 to-transparent" />
    </section>
  );
};

export default WhyChoose;